// Three-way viewport toggle (desktop / tablet / mobile). Markup.io-style.
// The actual width is applied by the parent so the iframe doesn't remount
// when the user switches viewports — see ReviewSurface.

import type { ReviewMode } from "@clarity/shared";

export type Viewport = "desktop" | "tablet" | "mobile";

// CSS widths. "desktop" means fill — the parent renders the iframe edge-
// to-edge. Tablet/mobile values match common breakpoint expectations
// rather than physical devices (iPad mini logical width / iPhone 14 Pro).
export const VIEWPORT_WIDTH: Record<Exclude<Viewport, "desktop">, number> = {
  tablet: 768,
  mobile: 390,
};

type Props = {
  value: Viewport;
  onChange: (v: Viewport) => void;
  // Optional comments toggle. Replaces the older focus-mode button —
  // now the only chrome that the reviewer can show/hide from the toolbar
  // is the comments sidebar. When open, the comment-bubble icon inverts
  // (black background) like the active viewport pill.
  commentsOpen?: boolean;
  onToggleComments?: () => void;
  // Optional badge for the comments toggle — typically the open-comment
  // count for the current page so the reviewer notices at a glance.
  commentsCount?: number;
  // Optional reload button. Sits immediately after the viewport-size
  // group so it's visually associated with "what's in the frame".
  onReload?: () => void;
  // Owner-only review mode picker (Comment / Code / Editor). When set,
  // a segmented control sits on the left of the toolbar — mirrors the
  // comments-bubble pill on the right. Editor is shown disabled with a
  // "Coming soon" title.
  mode?: ReviewMode;
  onChangeMode?: (mode: ReviewMode) => void;
};

export function ViewportToolbar({
  value,
  onChange,
  commentsOpen,
  onToggleComments,
  commentsCount,
  onReload,
  mode,
  onChangeMode,
}: Props) {
  return (
    <div
      data-tour-id="viewport"
      className="relative flex h-10 items-center justify-center gap-1 border-b border-neutral-200 bg-white"
    >
      {mode && onChangeMode ? (
        <div
          data-tour-id="mode-picker"
          className="absolute left-2 inline-flex rounded-md bg-neutral-100 p-0.5 text-xs"
        >
          <ModeButton
            active={mode === "view"}
            onClick={() => onChangeMode("view")}
            label="View"
            title="Explore the page — comments and code tools are paused"
          />
          <ModeButton
            active={mode === "comment"}
            onClick={() => onChangeMode("comment")}
            label="Comment"
          />
          <ModeButton
            active={mode === "code"}
            onClick={() => onChangeMode("code")}
            label="Code"
          />
          <ModeButton
            active={mode === "editor"}
            onClick={() => undefined}
            label="Editor"
            disabled
            title="Coming soon"
          />
        </div>
      ) : null}
      <ViewportButton
        active={value === "desktop"}
        onClick={() => onChange("desktop")}
        label="Desktop"
      >
        <DesktopIcon />
      </ViewportButton>
      <ViewportButton
        active={value === "tablet"}
        onClick={() => onChange("tablet")}
        label="Tablet"
      >
        <TabletIcon />
      </ViewportButton>
      <ViewportButton
        active={value === "mobile"}
        onClick={() => onChange("mobile")}
        label="Mobile"
      >
        <MobileIcon />
      </ViewportButton>
      {onReload ? (
        <button
          type="button"
          onClick={onReload}
          aria-label="Reload page"
          title="Reload the proxied page"
          className="ml-2 flex h-7 w-8 items-center justify-center rounded-md text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900"
        >
          <ReloadIcon />
        </button>
      ) : null}
      {onToggleComments ? (
        <button
          type="button"
          onClick={onToggleComments}
          aria-label={commentsOpen ? "Close comments" : "Open comments"}
          aria-pressed={!!commentsOpen}
          title={commentsOpen ? "Close comments" : "Open comments"}
          data-tour-id="comments-toggle"
          className={
            "absolute right-2 flex h-7 min-w-[2rem] items-center justify-center gap-1 rounded-md px-1.5 transition " +
            (commentsOpen
              ? "bg-neutral-900 text-white"
              : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900")
          }
        >
          <CommentBubbleIcon />
          {typeof commentsCount === "number" && commentsCount > 0 ? (
            <span
              className={
                "rounded-full px-1 text-[10px] font-semibold leading-tight " +
                (commentsOpen
                  ? "bg-white/20 text-white"
                  : "bg-neutral-200 text-neutral-700")
              }
            >
              {commentsCount}
            </span>
          ) : null}
        </button>
      ) : null}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  label,
  disabled = false,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={active}
      title={title}
      className={
        "rounded px-2.5 py-1 transition " +
        (active
          ? "bg-neutral-900 text-white"
          : disabled
            ? "text-neutral-300"
            : "text-neutral-600 hover:text-neutral-900")
      }
    >
      {label}
    </button>
  );
}

function ViewportButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={
        "flex h-7 w-8 items-center justify-center rounded-md transition " +
        (active
          ? "bg-neutral-900 text-white"
          : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900")
      }
    >
      {children}
    </button>
  );
}

function DesktopIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

function TabletIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="2.5" width="14" height="19" rx="2" />
      <path d="M11 18.5h2" />
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <path d="M11 18.5h2" />
    </svg>
  );
}

// Circular arrow — universal "reload / refresh" affordance.
function ReloadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.3" />
      <path d="M21 4v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3" />
      <path d="M3 20v-5h5" />
    </svg>
  );
}

// Speech-bubble icon — "toggle the comments sidebar". Replaces the
// previous focus-mode (corner brackets) affordance now that the
// comments panel is the only sidebar that toggles from this toolbar.
function CommentBubbleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4 3v-3H6.5A2.5 2.5 0 0 1 4 14.5v-8z" />
    </svg>
  );
}
